import { Injectable } from '@angular/core';

/**
 * Interface for basic confirmation PDF parameters
 */
export interface ConfirmacionPdfParams {
  idTrosten: string;
  contactoNombre: string;
  clienteNombre: string;
  contactoTelefono: string;
  numeroUnidades: number;
  tipoUnidad: string;
  numeroPasajeros: number;
  indicacionesEspeciales: string;
  notas: string;
  aliasOrigen: string;
  ciudadOrigen: string;
  direccionOrigen: string;
  aliasDestino: string;
  ciudadDestino: string;
  direccionDestino: string;
  regresoAOrigen: number;
  inicioServicio: number;
  finServicio: number;
  tipoDeVenta: string;
}

/**
 * Interface for confirmation PDF with cost parameters
 */
export interface ConfirmacionConCostoParams extends ConfirmacionPdfParams {
  total: number;
  montoPagado: number;
  numeroCliente: string;
}

/**
 * Service for building PDF URL parameters
 *
 * Constructs URL query strings for PDF generation endpoints
 * with proper encoding and parameter formatting.
 *
 * @example
 * constructor(private pdfBuilder: PdfDataBuilderService) {}
 *
 * const url = this.pdfBuilder.buildConfirmacionPdfUrl(params);
 * window.open(url, '_blank');
 */
@Injectable()
export class PdfDataBuilderService {

  private readonly BASE_PDF_URL = 'https://operacion.trosten.com/pdf-service/confirmacion-pdf.php';
  private readonly BASE_PDF_CON_COSTO_URL = 'https://operacion.trosten.com/pdf-service/confirmacion-con-costo-pdf.php';

  constructor() { }

  /**
   * Builds URL for basic confirmation PDF
   *
   * @param params - Confirmation PDF parameters
   * @returns Complete URL with query parameters
   *
   * @example
   * const params: ConfirmacionPdfParams = {
   *   idTrosten: 'VTC-25001',
   *   contactoNombre: 'Juan Pérez',
   *   // ... other params
   * };
   * const url = buildConfirmacionPdfUrl(params);
   * // Returns: "https://operacion.trosten.com/pdf-service/confirmacion-pdf.php?nC=VTC-25001&..."
   */
  buildConfirmacionPdfUrl(params: ConfirmacionPdfParams): string {
    const queryParams = new URLSearchParams();
    queryParams.append('nC', params.idTrosten);
    queryParams.append('co', params.contactoNombre);
    queryParams.append('con', params.clienteNombre);
    queryParams.append('t', params.contactoTelefono);
    queryParams.append('n', params.numeroUnidades.toString());
    queryParams.append('ti', params.tipoUnidad);
    queryParams.append('ca', params.numeroPasajeros.toString());
    queryParams.append('in', params.indicacionesEspeciales || '');
    queryParams.append('nt', params.notas || '');
    queryParams.append('tr', 'NO APLICA');
    queryParams.append('o', params.aliasOrigen);
    queryParams.append('oC', params.ciudadOrigen);
    queryParams.append('oD', params.direccionOrigen);
    queryParams.append('d', params.aliasDestino);
    queryParams.append('dC', params.ciudadDestino);
    queryParams.append('dD', params.direccionDestino);
    queryParams.append('rAO', params.regresoAOrigen.toString());
    queryParams.append('i', params.inicioServicio.toString());
    queryParams.append('f', params.finServicio.toString());
    queryParams.append('tv', params.tipoDeVenta);

    return `${this.BASE_PDF_URL}?${queryParams.toString()}`;
  }

  /**
   * Builds URL for confirmation PDF with cost information
   *
   * @param params - Confirmation PDF with cost parameters
   * @returns Complete URL with query parameters including cost data
   *
   * @example
   * const params: ConfirmacionConCostoParams = {
   *   // ... basic params
   *   total: 15000,
   *   montoPagado: 7500,
   *   numeroCliente: 'CLI-001'
   * };
   * const url = buildConfirmacionConCostoPdfUrl(params);
   */
  buildConfirmacionConCostoPdfUrl(params: ConfirmacionConCostoParams): string {
    const queryParams = new URLSearchParams();
    queryParams.append('nC', params.idTrosten);
    queryParams.append('co', params.contactoNombre);
    queryParams.append('con', params.clienteNombre);
    queryParams.append('t', params.contactoTelefono);
    queryParams.append('n', params.numeroUnidades.toString());
    queryParams.append('ti', params.tipoUnidad);
    queryParams.append('ca', params.numeroPasajeros.toString());
    queryParams.append('in', params.indicacionesEspeciales || '');
    queryParams.append('nt', params.notas || '');
    queryParams.append('tr', 'NO APLICA');
    queryParams.append('o', params.aliasOrigen);
    queryParams.append('oC', params.ciudadOrigen);
    queryParams.append('oD', params.direccionOrigen);
    queryParams.append('d', params.aliasDestino);
    queryParams.append('dC', params.ciudadDestino);
    queryParams.append('dD', params.direccionDestino);
    queryParams.append('rAO', params.regresoAOrigen.toString());
    queryParams.append('i', params.inicioServicio.toString());
    queryParams.append('f', params.finServicio.toString());
    queryParams.append('tv', params.tipoDeVenta);
    queryParams.append('ttl', params.total.toString());
    queryParams.append('mntPgd', params.montoPagado.toString());
    queryParams.append('nCl', params.numeroCliente);

    return `${this.BASE_PDF_CON_COSTO_URL}?${queryParams.toString()}`;
  }

  /**
   * Extracts contact information from contacts array
   *
   * @param contactos - Array of contacts
   * @param contratanteId - ID of the contratante contact
   * @returns Contact object with nombre and telefono
   */
  extractContactoInfo(contactos: any[], contratanteId: string): { nombre: string, telefono: string } {
    const contacto = contactos.find(c => c.$key === contratanteId);

    return {
      nombre: contacto ? contacto.nombre || '' : '',
      telefono: contacto ? contacto.telefono || '' : ''
    };
  }

  /**
   * Extracts unit information from servicio combinacion
   *
   * @param servConfirmacion - Service from confirmacion.servicios
   * @returns Object with numeroUnidades, tipoUnidad, and indicacionesEspeciales
   */
  extractUnidadInfo(servConfirmacion: any): {
    numeroUnidades: number,
    tipoUnidad: string,
    indicacionesEspeciales: string
  } {
    let numeroUnidades = 0;
    let tipoUnidad = '';
    const indicacionesEspeciales = servConfirmacion.indicacionesEspeciales || '';

    if (servConfirmacion.combinacion && servConfirmacion.combinacion.unidades) {
      for (const indiceUnidad in servConfirmacion.combinacion.unidades) {
        if (servConfirmacion.combinacion.unidades.hasOwnProperty(indiceUnidad)) {
          const unidad = servConfirmacion.combinacion.unidades[indiceUnidad];
          numeroUnidades = unidad.numeroDeUnidades || unidad;
          tipoUnidad = indiceUnidad;
          break; // Get first unit
        }
      }
    }

    return { numeroUnidades, tipoUnidad, indicacionesEspeciales };
  }

  /**
   * Extracts service information from servicios array
   *
   * @param servicios - Array of servicios from confirmation
   * @returns Object with all service-related fields
   */
  extractServicioInfo(servicios: any[]): {
    numeroPasajeros: number,
    tipoDeVenta: string,
    inicioServicio: number,
    finServicio: number,
    regresoAOrigen: number,
    aliasDestino: string,
    aliasOrigen: string,
    dirDestino: string,
    dirOrigen: string,
    notas: string
  } {
    if (!servicios || servicios.length === 0) {
      return {
        numeroPasajeros: 0,
        tipoDeVenta: '',
        inicioServicio: 0,
        finServicio: 0,
        regresoAOrigen: 0,
        aliasDestino: '',
        aliasOrigen: '',
        dirDestino: '',
        dirOrigen: '',
        notas: ''
      };
    }

    const servicio = servicios[0]; // Get first service

    return {
      numeroPasajeros: servicio.cantidadDePasajeros || 0,
      tipoDeVenta: servicio.tipoDeVenta || '',
      inicioServicio: servicio.inicioDeServicio || 0,
      finServicio: servicio.finDeServicio || 0,
      regresoAOrigen: servicio.regresoAOrigen || 0,
      aliasDestino: servicio.destino && servicio.destino.alias || '',
      aliasOrigen: servicio.origen && servicio.origen.alias || '',
      dirDestino: servicio.destino && servicio.destino.direccion || '',
      dirOrigen: servicio.origen && servicio.origen.direccion || '',
      notas: servicio.notas || ''
    };
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/pdf-data-builder.service.ts